import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import useAuthStore from "../store/useAuthStore";
import {
  createProfile,
  deleteProfile,
  getProfileOptions,
  getProfiles,
  setDefaultProfile,
  updateProfile,
} from "../services/ProfileServices";

const initialFormState = {
  name: "",
  emailTemplateId: "",
  subscriberListId: "",
  whatsappTemplateName: "",
  whatsappCampaignName: "",
  isDefault: false,
};

const getSelectedOptionName = (items, selectedId) => {
  if (!selectedId) return "Not set";

  const selectedOption = items.find(
    (item) => String(item.id) === String(selectedId)
  );

  return selectedOption?.name || "Unavailable";
};

const CampaignProfilesPage = () => {
  const { token } = useAuthStore((state) => state);
  const [profiles, setProfiles] = useState([]);
  const [options, setOptions] = useState({
    emailTemplates: [],
    subscriberLists: [],
    subscriberSegments: [],
    whatsappTemplates: [],
  });
  const [formState, setFormState] = useState(initialFormState);
  const [editingId, setEditingId] = useState("");
  const [loading, setLoading] = useState(false);

  const allListsAndSegments = [
    ...options.subscriberLists,
    ...options.subscriberSegments,
  ];

  const loadData = async () => {
    const [optionsRes, profilesRes] = await Promise.all([
      getProfileOptions(),
      getProfiles(),
    ]);
    setOptions({
      emailTemplates: optionsRes?.data?.emailTemplates || [],
      subscriberLists: optionsRes?.data?.subscriberLists || [],
      subscriberSegments: optionsRes?.data?.subscriberSegments || [],
      whatsappTemplates: optionsRes?.data?.whatsappTemplates || [],
    });
    setProfiles(profilesRes?.data?.profiles || []);
  };

  useEffect(() => {
    if (!token) return;

    loadData().catch((error) => {
      toast.error(error?.response?.data?.message || "Failed to load profiles");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const resetForm = () => {
    setFormState(initialFormState);
    setEditingId("");
  };

  const handleSubmit = async () => {
    if (!formState.name || !formState.emailTemplateId) {
      toast.error("Profile name and email template are required");
      return;
    }

    setLoading(true);
    try {
      if (editingId) {
        await updateProfile(editingId, formState);
        toast.success("Profile updated");
      } else {
        await createProfile(formState);
        toast.success("Profile created");
      }

      await loadData();
      resetForm();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save profile");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (profile) => {
    setEditingId(profile.id);
    setFormState({
      name: profile.name || "",
      emailTemplateId: profile.emailTemplateId || "",
      subscriberListId: profile.subscriberListId || "",
      whatsappTemplateName: profile.whatsappTemplateName || "",
      whatsappCampaignName: profile.whatsappCampaignName || "",
      isDefault: Boolean(profile.isDefault),
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this profile?")) return;

    try {
      await deleteProfile(id);
      toast.success("Profile deleted");
      await loadData();
      if (editingId === id) resetForm();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete profile");
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await setDefaultProfile(id);
      toast.success("Default profile updated");
      await loadData();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to update default profile"
      );
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4">Campaign Profiles</h2>
      <p className="text-sm text-gray-600 mb-6">
        Save a preset of Email Template + Subscriber List + WhatsApp Template.
      </p>

      <div className="bg-white rounded-lg border p-4 mb-6">
        <h3 className="font-semibold mb-3">
          {editingId ? "Edit Profile" : "Create Profile"}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            className="border rounded p-2"
            placeholder="Profile name"
            value={formState.name}
            onChange={(e) =>
              setFormState((prev) => ({ ...prev, name: e.target.value }))
            }
          />

          <select
            className="border rounded p-2"
            value={formState.emailTemplateId}
            onChange={(e) =>
              setFormState((prev) => ({
                ...prev,
                emailTemplateId: e.target.value,
              }))
            }
          >
            <option value="">Select Email Template</option>
            {options.emailTemplates.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>

          <select
            className="border rounded p-2"
            value={formState.subscriberListId}
            onChange={(e) =>
              setFormState((prev) => ({
                ...prev,
                subscriberListId: e.target.value,
              }))
            }
          >
            <option value="">Select Subscriber List (Optional)</option>
            {allListsAndSegments.map((item) => (
              <option key={`${item.type}-${item.id}`} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>

          <select
            className="border rounded p-2"
            value={formState.whatsappTemplateName}
            onChange={(e) =>
              setFormState((prev) => ({
                ...prev,
                whatsappTemplateName: e.target.value,
              }))
            }
          >
            <option value="">Select WhatsApp Template (Optional)</option>
            {options.whatsappTemplates.map((item) => (
              <option key={item.id} value={item.name}>
                {item.name}
                {item.status ? ` (${item.status})` : ""}
              </option>
            ))}
          </select>

          <input
            className="border rounded p-2"
            placeholder="WhatsApp campaign name (optional)"
            value={formState.whatsappCampaignName}
            onChange={(e) =>
              setFormState((prev) => ({
                ...prev,
                whatsappCampaignName: e.target.value,
              }))
            }
          />

          <label className="inline-flex items-center gap-2 mt-2">
            <input
              type="checkbox"
              checked={formState.isDefault}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  isDefault: e.target.checked,
                }))
              }
            />
            Set as default
          </label>
        </div>

        <div className="flex gap-2 mt-4">
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-60"
            disabled={loading}
            onClick={handleSubmit}
          >
            {editingId ? "Update Profile" : "Create Profile"}
          </button>
          {editingId ? (
            <button
              className="bg-gray-500 text-white px-4 py-2 rounded"
              onClick={resetForm}
            >
              Cancel Edit
            </button>
          ) : null}
        </div>
      </div>

      <div className="bg-white rounded-lg border p-4">
        <h3 className="font-semibold mb-3">Saved Profiles</h3>
        {profiles.length === 0 ? (
          <p className="text-sm text-gray-500">No profiles yet.</p>
        ) : (
          <div className="space-y-3">
            {profiles.map((profile) => (
              <div
                key={profile.id}
                className="border rounded p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2"
              >
                <div>
                  <div className="font-medium">
                    {profile.name}{" "}
                    {profile.isDefault ? (
                      <span className="text-xs text-green-700 bg-green-100 px-2 py-1 rounded">
                        Default
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-gray-500">
                    Email Template:{" "}
                    {getSelectedOptionName(
                      options.emailTemplates,
                      profile.emailTemplateId
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    Subscriber List:{" "}
                    {getSelectedOptionName(
                      allListsAndSegments,
                      profile.subscriberListId
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    WhatsApp Template: {profile.whatsappTemplateName || "Not set"}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    className="bg-indigo-500 text-white px-3 py-1 rounded"
                    onClick={() => handleEdit(profile)}
                  >
                    Edit
                  </button>
                  {!profile.isDefault ? (
                    <button
                      className="bg-emerald-600 text-white px-3 py-1 rounded"
                      onClick={() => handleSetDefault(profile.id)}
                    >
                      Make Default
                    </button>
                  ) : null}
                  <button
                    className="bg-red-600 text-white px-3 py-1 rounded"
                    onClick={() => handleDelete(profile.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignProfilesPage;
